import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { LoginForm } from '@/components/LoginForm';
import { Logo } from '@/components/Logo';
import { useTheme } from '@/components/ThemeProvider';
import { useTranslation } from '@/components/i18n/Translator';

import { useQuery } from 'hooks/query';

import { ChainlitContext, useAuth } from 'client-types/*';

// LoginError will be created in the component to use translation
export const createLoginError = (message: string) => new Error(message);

export default function Login() {
  const query = useQuery();
  const { data: config, user, setUserFromAPI } = useAuth();
  const [error, setError] = useState('');
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  const { variant } = useTheme();
  const isDarkMode = variant === 'dark';
  const { t } = useTranslation();

  const handleCookieAuth = (json: any): void => {
    if (json?.success != true)
      throw createLoginError(t('auth.errors.loginGeneral'));

    // Validate login cookie and get user data.
    setUserFromAPI();
  };

  const handleAuth = async (
    jsonPromise: Promise<any>,
    redirectURL?: string
  ) => {
    try {
      const json = await jsonPromise;

      handleCookieAuth(json);

      if (redirectURL) {
        navigate(redirectURL);
      }
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleHeaderAuth = async () => {
    const jsonPromise = apiClient.headerAuth();

    // Why does apiClient redirect to '/' but handlePasswordLogin to callbackUrl?
    await handleAuth(jsonPromise, '/');
  };

  const handlePasswordLogin = async (email: string, password: string) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const jsonPromise = apiClient.passwordAuth(formData);
    await handleAuth(jsonPromise);
  };

  useEffect(() => {
    setError(query.get('error') || '');
  }, [query]);

  useEffect(() => {
    if (!config) {
      return;
    }
    if (!config.requireLogin) {
      navigate('/');
    }
    if (config.headerAuth && !user) {
      handleHeaderAuth();
    }
    if (user) {
      // Handle returnTo parameter after successful login
      const returnTo = sessionStorage.getItem('returnTo') || 
                       query.get('returnTo');
      
      if (returnTo) {
        sessionStorage.removeItem('returnTo');
        navigate(returnTo);
      } else {
        navigate('/');
      }
    }
  }, [config, user, query]);

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Logo className="w-[150px]" />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <LoginForm
              error={error}
              callbackUrl="/"
              providers={config?.oauthProviders || []}
              onPasswordSignIn={
                config?.passwordAuth ? handlePasswordLogin : undefined
              }
              onOAuthSignIn={async (provider: string) => {
                window.location.href = apiClient.getOAuthEndpoint(provider);
              }}
            />
            {config?.passwordAuth && (
              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('auth.signup.prompt')}{' '}
                  <Link
                    to="/signup"
                    className="text-primary hover:underline font-medium"
                  >
                    {t('auth.signup.link')}
                  </Link>
                </p>
              </div>
            )}

            {/* Company Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="text-center space-y-1">
                <p className="text-xs text-muted-foreground">
                  {t('auth.footer.developedBy')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('auth.footer.orgNumber')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('auth.footer.copyright')}
                </p>
                <p className="text-xs">
                  <a
                    href="mailto:info@vedx.se"
                    className="text-primary hover:underline"
                  >
                    {t('auth.footer.email')}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!config?.headerAuth ? (
        <div className="relative hidden bg-muted lg:block overflow-hidden">
          <img
            src={
              config?.ui?.login_page_image ||
              apiClient.buildEndpoint('/favicon')
            }
            alt="Image"
            className={`absolute inset-0 h-full w-full object-cover ${
              isDarkMode
                ? config?.ui?.login_page_image_dark_filter ||
                  'brightness-[0.2] grayscale'
                : config?.ui?.login_page_image_filter || ''
            }`}
          />
        </div>
      ) : null}
    </div>
  );
}
